// skipcq: JS-W1028, JS-0128
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import StickyButtonBar from '../../components/common/StickyButtonBar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import Alert from '../../components/widgets/Alert';
import useCompanyLocationRecords from '../../hooks/useCompanyLocationRecords';
import { useCompanyLocationRecordsDelete } from '../../hooks/useCompanyLocationRecordsDelete';
import { useCompanyLocationRecordsReassign } from '../../hooks/useCompanyLocationRecordsReassign';
import { useBreweriesSearch } from '../../hooks/useBreweriesSearch';

function formatRecordAddress(record) {
  if (record.source === 'googleGeocode') {
    const geoAddress =
      record.geoMeta?.geocodeAddress || record.geoMeta?.geocode_address || '';
    if (geoAddress) {
      return geoAddress;
    }
  }
  return [record.address1, record.city, record.state, record.zip]
    .filter(Boolean)
    .join(', ');
}

function formatRecordDate(dateValue) {
  if (!dateValue) {
    return '';
  }
  return new Date(dateValue).toLocaleDateString();
}

function RecordsTableHead({ allSelected, someSelected, onHeaderCheckboxChange }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={someSelected}
            checked={allSelected}
            onChange={onHeaderCheckboxChange}
            inputProps={{ 'aria-label': 'Select all records' }}
          />
        </TableCell>
        <TableCell>Name</TableCell>
        <TableCell>Address</TableCell>
        <TableCell>Website</TableCell>
        <TableCell>Phone</TableCell>
        <TableCell>Source</TableCell>
        <TableCell>Date</TableCell>
      </TableRow>
    </TableHead>
  );
}

function RecordWebsiteCell({ websiteUrl }) {
  if (!websiteUrl) {
    return <TableCell />;
  }
  return (
    <TableCell>
      <Link href={websiteUrl} target="_blank" rel="noopener noreferrer">
        {websiteUrl}
      </Link>
    </TableCell>
  );
}

function RecordsTableRow({ record, isSelected, onRowCheckboxChange }) {
  return (
    <TableRow hover>
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={onRowCheckboxChange}
          inputProps={{
            'data-id': record.id,
            'aria-label': `Select record ${record.id}`,
          }}
        />
      </TableCell>
      <TableCell>{record.name || ''}</TableCell>
      <TableCell>{formatRecordAddress(record)}</TableCell>
      <RecordWebsiteCell websiteUrl={record.website || ''} />
      <TableCell>{record.phone || ''}</TableCell>
      <TableCell>{record.source || ''}</TableCell>
      <TableCell>{formatRecordDate(record.date)}</TableCell>
    </TableRow>
  );
}

function RecordsTable({
  parentBreweryId,
  selectedRecordIds,
  onToggleRecord,
  onRegisterRecordsRefresh,
}) {
  const { records, loading, error, refresh } =
    useCompanyLocationRecords(parentBreweryId);
  const selectedIds = Array.isArray(selectedRecordIds)
    ? selectedRecordIds
    : [];

  useEffect(() => {
    if (onRegisterRecordsRefresh && typeof refresh === 'function') {
      onRegisterRecordsRefresh(refresh);
    }
  }, [onRegisterRecordsRefresh, refresh]);

  const handleRowCheckboxChange = useCallback(
    (event) => {
      if (!onToggleRecord) return;
      const { checked, dataset } = event.target;
      const id = dataset?.id;
      if (!id) return;
      onToggleRecord(id, checked);
    },
    [onToggleRecord],
  );

  const handleHeaderCheckboxChange = useCallback(
    (event) => {
      if (!onToggleRecord || !Array.isArray(records) || !records.length) return;
      const { checked } = event.target;
      records.forEach((record) => {
        if (!record?.id) return;
        onToggleRecord(record.id, checked);
      });
    },
    [onToggleRecord, records],
  );

  const allSelected = Boolean(
    records &&
      records.length > 0 &&
      records.every((record) => selectedIds.includes(record.id)),
  );

  const someSelected = Boolean(
    records && records.length > 0 && selectedIds.length > 0 && !allSelected,
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 2
        }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        p: 2
      }}>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!records.length) {
    return (
      <Box sx={{
        p: 2
      }}>
        <Typography variant="body2">No records found.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="Brewery location records">
        <RecordsTableHead
          allSelected={allSelected}
          someSelected={someSelected}
          onHeaderCheckboxChange={handleHeaderCheckboxChange}
        />
        <TableBody>
          {records.map((record) => (
            <RecordsTableRow
              key={record.id}
              record={record}
              isSelected={selectedIds.includes(record.id)}
              onRowCheckboxChange={handleRowCheckboxChange}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

RecordsTableHead.propTypes = {
  allSelected: PropTypes.bool.isRequired,
  someSelected: PropTypes.bool.isRequired,
  onHeaderCheckboxChange: PropTypes.func.isRequired,
};

RecordWebsiteCell.propTypes = {
  websiteUrl: PropTypes.string.isRequired,
};

RecordsTableRow.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    website: PropTypes.string,
    phone: PropTypes.string,
    source: PropTypes.string,
    date: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onRowCheckboxChange: PropTypes.func.isRequired,
};

RecordsTable.propTypes = {
  parentBreweryId: PropTypes.string.isRequired,
  selectedRecordIds: PropTypes.arrayOf(PropTypes.string),
  onToggleRecord: PropTypes.func,
  onRegisterRecordsRefresh: PropTypes.func,
};

/**
 * BreweryRecordsSection
 * Encapsulates the Records tab: selection, small action bar, delete and reassign flows.
 */
function BreweryRecordsSection({ parentBreweryId, breweryState }) {
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignSearchQuery, setReassignSearchQuery] = useState('');
  const [reassignSelectedTargetId, setReassignSelectedTargetId] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const recordsRefreshRef = useRef(null);

  const {
    deleteRecords,
    loading: deleteRecordsLoading,
    errorMessage: deleteRecordsErrorMessage,
    clearError: clearDeleteRecordsError,
  } = useCompanyLocationRecordsDelete(parentBreweryId);

  const {
    reassignRecords,
    loading: reassignRecordsLoading,
    errorMessage: reassignRecordsErrorMessage,
    clearError: clearReassignRecordsError,
  } = useCompanyLocationRecordsReassign(parentBreweryId);

  const {
    breweries: reassignBreweries,
    isLoading: reassignSearchLoading,
  } = useBreweriesSearch(breweryState || '', reassignSearchQuery, null);

  const handleToggleRecord = useCallback((recordId, selected) => {
    if (!recordId) return;
    setSelectedRecordIds((prev) => {
      if (selected) {
        if (prev.includes(recordId)) {
          return prev;
        }
        return [...prev, recordId];
      }
      return prev.filter((idValue) => idValue !== recordId);
    });
  }, []);

  const handleRegisterRecordsRefresh = useCallback((refreshFn) => {
    recordsRefreshRef.current = refreshFn;
  }, []);

  const handleOpenDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleConfirmDeleteRecords = useCallback(async () => {
    if (!selectedRecordIds.length) return;
    const result = await deleteRecords(selectedRecordIds);
    if (result?.deleted && result.deletedCount > 0) {
      if (recordsRefreshRef.current) {
        await recordsRefreshRef.current();
      }
      setSelectedRecordIds([]);
      setSuccessMessage(
        result.deletedCount === 1
          ? 'Record deleted successfully.'
          : `${result.deletedCount} records deleted successfully.`,
      );
      setSuccessOpen(true);
    } else if (result?.error) {
      setErrorOpen(true);
    }
    setDeleteDialogOpen(false);
  }, [deleteRecords, selectedRecordIds]);

  const handleOpenReassignDialog = useCallback(() => {
    setReassignDialogOpen(true);
  }, []);

  const handleCloseReassignDialog = useCallback(() => {
    setReassignDialogOpen(false);
  }, []);

  const handleReassignSearchChange = useCallback((event) => {
    setReassignSearchQuery(event.target.value);
  }, []);

  const handleReassignTargetClick = useCallback((event) => {
    const { id: targetId } = event.currentTarget.dataset || {};
    if (targetId) {
      setReassignSelectedTargetId(targetId);
    }
  }, []);

  const handleConfirmReassignRecords = useCallback(async () => {
    if (
      !selectedRecordIds.length ||
      !reassignSelectedTargetId ||
      reassignSelectedTargetId === parentBreweryId
    ) {
      return;
    }
    const result = await reassignRecords(
      reassignSelectedTargetId,
      selectedRecordIds,
    );
    if (result?.updated && result.updatedCount > 0) {
      if (recordsRefreshRef.current) {
        await recordsRefreshRef.current();
      }
      setSelectedRecordIds([]);
      setReassignDialogOpen(false);
      setSuccessMessage(
        result.updatedCount === 1
          ? 'Record reassigned successfully.'
          : `${result.updatedCount} records reassigned successfully.`,
      );
      setSuccessOpen(true);
    } else if (result?.error) {
      setErrorOpen(true);
    }
  }, [
    parentBreweryId,
    reassignRecords,
    reassignSelectedTargetId,
    selectedRecordIds,
  ]);

  const handleCloseErrorSnackbar = useCallback(() => {
    setErrorOpen(false);
    clearDeleteRecordsError();
    clearReassignRecordsError();
  }, [clearDeleteRecordsError, clearReassignRecordsError]);

  const handleCloseSuccessSnackbar = useCallback(() => {
    setSuccessOpen(false);
  }, []);

  const isBusy = deleteRecordsLoading || reassignRecordsLoading;

  const deleteDisabled = !selectedRecordIds.length || isBusy;

  const reassignToolbarDisabled = !selectedRecordIds.length || isBusy;

  const reassignConfirmDisabled =
    !selectedRecordIds.length ||
    isBusy ||
    !reassignSelectedTargetId ||
    reassignSelectedTargetId === parentBreweryId;

  const combinedErrorMessage = useMemo(
    () =>
      deleteRecordsErrorMessage ||
      reassignRecordsErrorMessage ||
      'An error occurred while updating records.',
    [deleteRecordsErrorMessage, reassignRecordsErrorMessage],
  );

  return (
    <>
      <StickyButtonBar>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenReassignDialog}
          disabled={reassignToolbarDisabled}
        >
          Reassign Records
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleOpenDeleteDialog}
          disabled={deleteDisabled}
        >
          Delete Records
        </Button>
      </StickyButtonBar>
      <RecordsTable
        parentBreweryId={parentBreweryId}
        selectedRecordIds={selectedRecordIds}
        onToggleRecord={handleToggleRecord}
        onRegisterRecordsRefresh={handleRegisterRecordsRefresh}
      />
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {combinedErrorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={handleCloseSuccessSnackbar}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-records-dialog-title"
        aria-describedby="delete-records-dialog-description"
      >
        <DialogTitle id="delete-records-dialog-title">
          Delete Records
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-records-dialog-description">
            Are you sure you want to delete{' '}
            {selectedRecordIds.length > 1
              ? `${selectedRecordIds.length} selected records`
              : 'the selected record'}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteRecords}
            color="error"
            variant="contained"
            disabled={deleteRecordsLoading}
            startIcon={
              deleteRecordsLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {deleteRecordsLoading ? 'Deleting...' : 'Delete Records'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={reassignDialogOpen}
        onClose={handleCloseReassignDialog}
        aria-labelledby="reassign-records-dialog-title"
        aria-describedby="reassign-records-dialog-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="reassign-records-dialog-title">
          Reassign Records
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText id="reassign-records-dialog-description">
            Select a new parent brewery to reassign the selected records to.
          </DialogContentText>
          <Box
            sx={{
              mt: 2,
              mb: 2
            }}>
            <TextField
              label="Search breweries"
              fullWidth
              value={reassignSearchQuery}
              onChange={handleReassignSearchChange}
              size="small"
            />
          </Box>
          <Box
            sx={{
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            {reassignSearchLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2
                }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense>
                {reassignBreweries
                  .filter((b) => b.id && b.id !== parentBreweryId)
                  .map((breweryCandidate) => {
                    const candidate = breweryCandidate.data || {};
                    const primary = candidate.name || breweryCandidate.id;
                    const secondaryParts = [
                      candidate.city,
                      candidate.state,
                    ].filter(Boolean);
                    const secondary = secondaryParts.join(', ');
                    return (
                      <ListItemButton
                        key={breweryCandidate.id}
                        data-id={breweryCandidate.id}
                        selected={
                          breweryCandidate.id === reassignSelectedTargetId
                        }
                        onClick={handleReassignTargetClick}
                      >
                        <ListItemText primary={primary} secondary={secondary} />
                      </ListItemButton>
                    );
                  })}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReassignDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReassignRecords}
            color="primary"
            variant="contained"
            disabled={reassignConfirmDisabled}
          >
            {reassignRecordsLoading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                &nbsp;Reassigning…
              </>
            ) : (
              'Reassign'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

BreweryRecordsSection.propTypes = {
  parentBreweryId: PropTypes.string.isRequired,
  breweryState: PropTypes.string,
};

export default BreweryRecordsSection;


